(function () {
  'use strict';

  const esc = s => String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function driveImageUrl(id) {
    if (!id) return '';
    // Direct image endpoint — serves a real image file, no Google
    // cookies/login needed, so it works in incognito & with 3P-cookie blocking
    return 'https://drive.google.com/thumbnail?id=' + id + '&sz=w1200';
  }

  function driveVideoEmbedUrl(id) {
    return 'https://drive.google.com/file/d/' + id + '/preview';
  }

  function parseVideoUrl(url) {
    if (!url) return null;
    const trimmed = String(url).trim();
    if (!trimmed) return null;

    const yt = trimmed.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (yt) {
      return {
        thumbnail: 'https://img.youtube.com/vi/' + yt[1] + '/hqdefault.jpg',
        embedUrl: 'https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&mute=1&rel=0&playsinline=1'
      };
    }

    const drive = trimmed.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([A-Za-z0-9_-]{20,})/);
    if (drive) {
      return {
        thumbnail: driveImageUrl(drive[1]),
        embedUrl: driveVideoEmbedUrl(drive[1])
      };
    }

    return null;
  }

  function normalizeStage(raw) {
    if (!raw) return '';
    const t = String(raw).toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
    const m = t.match(/stage\s*(\d)/);
    if (m) return 'Stage ' + m[1];
    if (t.includes('advanced')) return 'Advanced widening';
    if (t.includes('medium')) return 'Medium widening';
    if (t.includes('starting') || t.includes('widened')) return 'Starting widening';
    return String(raw);
  }

  // Cleans up inconsistent location/state strings coming from the sheet, e.g.:
  //   "Rajasthan/ Pan India"      -> "Rajasthan / Pan India"
  //   "Rajasthan /Pan India"      -> "Rajasthan / Pan India"
  //   "Hyderadbad/South India"    -> "Hyderabad / South India"  (typo fix)
  //   "Chennai/South India"       -> "Chennai / South India"
  //   "Lucknow,UP"                -> "Lucknow, UP"
  //   "  Gujarat  "               -> "Gujarat"
  // Does NOT bucket values into a fixed set of states — it preserves whatever
  // distinct location the sheet has, just normalized for consistent display/filtering.
  function normalizeState(raw) {
    if (!raw) return '';
    let t = String(raw).trim();
    if (!t) return '';

    // Fix known typos seen in the sheet
    t = t.replace(/Hyderadbad/gi, 'Hyderabad');

    // Normalize slash spacing: "X/Y", "X /Y", "X/ Y" -> "X / Y"
    t = t.replace(/\s*\/\s*/g, ' / ');

    // Normalize comma spacing: "X,Y" -> "X, Y"
    t = t.replace(/\s*,\s*/g, ', ');

    // Collapse any repeated/stray whitespace
    t = t.replace(/\s+/g, ' ').trim();

    return t;
  }

  function parseCSV(text) {
    const rows = [];
    let row = [], cell = '', inQ = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i], n = text[i + 1];
      if (inQ) {
        if (c === '"' && n === '"') { cell += '"'; i++; }
        else if (c === '"') inQ = false;
        else cell += c;
      } else {
        if (c === '"') inQ = true;
        else if (c === ',') { row.push(cell); cell = ''; }
        else if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
        else if (c !== '\r') cell += c;
      }
    }
    if (cell || row.length) { row.push(cell); rows.push(row); }

    const headers = (rows.shift() || []).map(h => h.trim());
    return rows
      .filter(r => r.some(v => v !== ''))
      .map(r => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (r[i] || '').trim(); });
        return obj;
      });
  }

  document.querySelectorAll('.ts-real-stories').forEach(initSection);

  function initSection(section) {
    const csvUrl = section.dataset.csvUrl;
    const sectionId = section.dataset.sectionId;
    const pageName = section.dataset.pageName || sectionId || '';
    const track = section.querySelector('[data-cards-track]');
    const dotsContainer = section.querySelector('[data-dots]');
    const statusEl = section.querySelector('[data-status]');
    const carouselWrap = section.querySelector('[data-carousel]');
    const filterCountEl = section.querySelector('[data-filter-count]');
    const overlay = document.querySelector('[data-filter-overlay][aria-labelledby="ts-filter-title-' + sectionId + '"]');
    const videoModal = document.querySelector('[data-video-modal]');
    const videoFrame = videoModal ? videoModal.querySelector('[data-video-frame]') : null;

    function trackEvent(eventName, params) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName, page_name: pageName }, params || {}));
    }

    const state = {
      all: [],
      filtered: [],
      displayed: [],
      filters: { age_bucket: [], stage: [], state: [], gender: [] }
    };

    if (!csvUrl) {
      statusEl.textContent = 'No CSV URL configured. Add it in the section settings.';
      return;
    }

    fetch(csvUrl)
      .then(r => {
        if (!r.ok) throw new Error('Failed to load sheet (' + r.status + ')');
        return r.text();
      })
      .then(text => {
        state.all = parseCSV(text)
          .filter(row => {
            const status = (row['Status'] || '').toLowerCase();
            return status === 'done' || status === 'approved';
          })
          .map(row => {
            const isValidDriveId = id => /^[A-Za-z0-9_-]{20,}$/.test(id);
            const imageIds = (row['image_ids'] || '').split(',').map(s => s.trim()).filter(isValidDriveId);
            const videoIds = (row['video_ids'] || '').split(',').map(s => s.trim()).filter(isValidDriveId);
            const finalLinkVideo = parseVideoUrl(row['Final Link']);
            const fallbackVideo = videoIds[0]
              ? { thumbnail: driveImageUrl(videoIds[0]), embedUrl: driveVideoEmbedUrl(videoIds[0]) }
              : null;
            const video = finalLinkVideo || fallbackVideo;
            return {
              name: (row['Customer Name'] || '').split(' ')[0] || row['Customer Name'],
              fullName: row['Customer Name'],
              age: parseInt(row['Age (Manual Entry)'], 10) || null,
              age_bucket: row['age_bucket'] || '',
              gender: row['Gender'] || '',
              stage: normalizeStage(row['Inferred Stage']),
              state: normalizeState(row['Inferred Location']),
              months: (row['months'] || '').replace(/[{}]/g, '').trim(),
              quote: row['One liner'] || '',
              imageIds: imageIds,
              videoIds: videoIds,
              hasVideo: !!video,
              videoThumb: video ? video.thumbnail : '',
              videoEmbed: video ? video.embedUrl : '',
              beforeImg: imageIds[1] ? driveImageUrl(imageIds[1]) : (imageIds[0] ? driveImageUrl(imageIds[0]) : ''),
              afterImg: imageIds[0] ? driveImageUrl(imageIds[0]) : ''
            };
          })
          .filter(t => t.quote && (t.beforeImg || t.hasVideo));

        state.filtered = [...state.all];
        statusEl.hidden = true;
        carouselWrap.hidden = false;
        renderCards();
      })
      .catch(err => {
        console.error('Real Stories error:', err);
        statusEl.textContent = 'Could not load stories right now.';
      });

    function getCardsPerView() { return window.innerWidth >= 768 ? 3 : 1; }

    function getDisplayList() {
      return state.filtered;
    }

    function renderCards() {
      state.displayed = getDisplayList();

      if (!state.displayed.length) {
        track.innerHTML = '<div class="ts-empty-state">No stories match your filters. Try clearing some.</div>';
        dotsContainer.innerHTML = '';
        return;
      }

      track.innerHTML = state.displayed.map((t, idx) => {
        const meta = t.stage
          ? esc(t.stage) + ' Hair Loss' + (t.months ? ' <span class="ts-arrow">→</span> Visible results in ' + esc(t.months) + ' months' : '')
          : (t.months ? 'Visible results in ' + esc(t.months) + ' months' : '');

        const ageDisplay = t.age ? ', ' + t.age : '';

        // Eager-load the first few cards (above the fold); lazy-load the rest.
        const loadingAttr = idx < 3 ? 'eager' : 'lazy';

        let cardMedia;
        if (t.hasVideo) {
          const thumbHtml = t.videoThumb
            ? '<img src="' + esc(t.videoThumb) + '" class="ts-img-full" alt="' + esc(t.fullName) + ' video testimonial" loading="' + loadingAttr + '">'
            : '<div class="ts-img-full ts-video-poster"></div>';
          cardMedia =
            thumbHtml +
            '<button class="ts-play-overlay" type="button" data-video-embed="' + esc(t.videoEmbed) + '" data-position="' + (idx + 1) + '" aria-label="Play ' + esc(t.fullName) + ' video">' +
              '<div class="ts-play-icon" aria-hidden="true"></div>' +
            '</button>';
        } else if (t.beforeImg) {
          cardMedia =
            '<img src="' + esc(t.beforeImg) + '" class="ts-img-half ts-pos-before" alt="' + esc(t.fullName) + ' before" loading="' + loadingAttr + '">' +
            '<img src="' + esc(t.afterImg) + '" class="ts-img-half ts-pos-after" alt="' + esc(t.fullName) + ' after" loading="' + loadingAttr + '">' +
            '<span class="ts-image-label ts-label-before">BEFORE</span>' +
            '<span class="ts-image-label ts-label-after">AFTER</span>';
        } else {
          cardMedia =
            '<div class="ts-img-half fallback-before"></div><div class="ts-img-half fallback-after"></div>' +
            '<span class="ts-image-label ts-label-before">BEFORE</span>' +
            '<span class="ts-image-label ts-label-after">AFTER</span>';
        }

        return '<article class="ts-card">' +
          '<div class="ts-card-image">' + cardMedia + '</div>' +
          '<div class="ts-card-body">' +
            '<div class="ts-card-name">' + esc(t.name) + ageDisplay + '</div>' +
            '<div class="ts-card-meta">' + meta + '</div>' +
            '<div class="ts-card-divider"></div>' +
            '<div class="ts-card-quote">"' + esc(t.quote) + '"</div>' +
          '</div>' +
        '</article>';
      }).join('');

      renderDots();
      updateActiveDot();
    }

    function renderDots() {
      const perView = getCardsPerView();
      const pages = Math.max(1, Math.ceil(state.displayed.length / perView));
      dotsContainer.innerHTML = Array.from({ length: pages }, (_, i) =>
        '<button class="ts-dot' + (i === 0 ? ' active' : '') + '" type="button" data-page="' + i + '" role="tab" aria-label="Page ' + (i + 1) + '"></button>'
      ).join('');
    }

    function updateActiveDot() {
      if (!track.children[0] || !track.children[0].offsetWidth) return;
      const perView = getCardsPerView();
      const cardWidth = track.children[0].offsetWidth + 14;
      const page = Math.round(track.scrollLeft / (cardWidth * perView));
      dotsContainer.querySelectorAll('.ts-dot').forEach((d, i) => {
        d.classList.toggle('active', i === page);
      });
    }

    track.addEventListener('scroll', () => requestAnimationFrame(updateActiveDot));

    // Retry failed Drive thumbnails once (handles 429 rate-limiting),
    // then fall back to the gradient placeholder.
    // Note: 'true' (capture) is required — img error events don't bubble.
    track.addEventListener('error', e => {
      const img = e.target;
      if (!img || img.tagName !== 'IMG' || !img.getAttribute('src')) return;

      if (!img.dataset.retried) {
        // First failure -> retry once after a random 1.5-3s delay
        // so retries don't re-trigger Google's rate limit in a burst.
        img.dataset.retried = '1';
        const src = img.src;
        img.dataset.origSrc = src;
        img.removeAttribute('src');
        setTimeout(() => { img.src = src; }, 1500 + Math.random() * 1500);
      } else {
        // Second failure -> log it and show the gradient fallback.
        console.warn('[real-stories] image failed permanently:', img.dataset.origSrc, '(' + img.alt + ')');
        if (img.classList.contains('ts-img-full')) {
          img.classList.add('ts-video-poster');
        } else if (img.classList.contains('ts-pos-before')) {
          img.classList.add('fallback-before');
        } else {
          img.classList.add('fallback-after');
        }
        img.removeAttribute('src');
      }
    }, true);

    dotsContainer.addEventListener('click', e => {
      const dot = e.target.closest('.ts-dot');
      if (!dot) return;
      const perView = getCardsPerView();
      const cardWidth = track.children[0].offsetWidth + 14;
      track.scrollTo({ left: cardWidth * perView * +dot.dataset.page, behavior: 'smooth' });
    });

    track.addEventListener('click', e => {
      const play = e.target.closest('[data-video-embed]');
      if (!play || !videoModal || !videoFrame) return;
      const embedUrl = play.dataset.videoEmbed;
      const position = parseInt(play.dataset.position, 10) || 0;
      trackEvent('testimonial_played', { testimonial_position: position });
      videoFrame.innerHTML = '<iframe src="' + esc(embedUrl) +
        '" allow="autoplay; encrypted-media" allowfullscreen></iframe>';
      videoModal.classList.add('open');
      videoModal.hidden = false;
      document.body.style.overflow = 'hidden';
    });

    if (videoModal) {
      const closeVideo = () => {
        videoModal.classList.remove('open');
        videoModal.hidden = true;
        if (videoFrame) videoFrame.innerHTML = '';
        document.body.style.overflow = '';
      };
      videoModal.querySelector('[data-close-video]').addEventListener('click', closeVideo);
      videoModal.addEventListener('click', e => { if (e.target === videoModal) closeVideo(); });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && videoModal.classList.contains('open')) closeVideo();
      });
    }

    // "state" values are normalized full strings like "Rajasthan / Pan India",
    // but the filter pills use short values like "Rajasthan". So the state filter
    // matches on a startsWith basis; every other filter still matches exactly.
    function matchesFilter(item, key, value) {
      if (key === 'state') {
        return !!item.state && item.state.toLowerCase().startsWith(value.toLowerCase());
      }
      if (key === 'age_bucket') {
        // Match the numeric age against the pill's range (e.g. "18-24"),
        // instead of relying on the sheet's age_bucket column.
        if (item.age == null) return false;
        const nums = (value.match(/\d+/g) || []).map(Number);
        if (!nums.length) return false;
        const lo = Math.min(...nums);
        const hi = nums.length > 1 ? Math.max(...nums) : Infinity;
        return item.age >= lo && item.age <= hi;
      }

      return item[key] === value;
    }

    function applyFilters() {
      state.filtered = state.all.filter(t =>
        Object.keys(state.filters).every(k =>
          state.filters[k].length === 0 || state.filters[k].some(v => matchesFilter(t, k, v))
        )
      );
      track.scrollLeft = 0;
      renderCards();
      updateFilterCount();
      trackEvent('testimonial_filter_applied');
    }

    function updateFilterCount() {
      const total = Object.values(state.filters).reduce((sum, arr) => sum + arr.length, 0);
      if (!filterCountEl) return;
      if (total === 0) {
        filterCountEl.hidden = true;
      } else {
        filterCountEl.hidden = false;
        filterCountEl.textContent = total;
      }
    }

    if (overlay) {
      overlay.querySelectorAll('.ts-filter-pill').forEach(pill => {
        pill.addEventListener('click', () => {
          const group = pill.closest('.ts-filter-pills').dataset.filter;
          const value = pill.dataset.value;
          const arr = state.filters[group];
          const idx = arr.indexOf(value);
          if (idx > -1) { arr.splice(idx, 1); pill.classList.remove('active'); }
          else { arr.push(value); pill.classList.add('active'); }
        });
      });

      const openBtn = section.querySelector('[data-open-filters]');
      const closeModal = () => {
        overlay.classList.remove('open');
        overlay.hidden = true;
        document.body.style.overflow = '';
      };

      openBtn.addEventListener('click', () => {
        overlay.classList.add('open');
        overlay.hidden = false;
        document.body.style.overflow = 'hidden';
      });
      overlay.querySelector('[data-close-filters]').addEventListener('click', closeModal);
      overlay.querySelector('[data-apply-filters]').addEventListener('click', () => {
        applyFilters();
        closeModal();
      });
      overlay.querySelector('[data-clear-filters]').addEventListener('click', () => {
        Object.keys(state.filters).forEach(k => state.filters[k] = []);
        overlay.querySelectorAll('.ts-filter-pill').forEach(p => p.classList.remove('active'));
        applyFilters();
      });
      overlay.addEventListener('click', e => { if (e.target === overlay) closeModal(); });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && overlay.classList.contains('open')) closeModal();
      });
    }

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        renderCards();
      }, 150);
    });
  }
})();